import {ChevronRightIcon} from '@heroicons/react/24/solid';
import React from 'react';
import {Link, useLocation, useParams} from 'react-router-dom';

import * as tw from 'src/lib/tailwindClasses';

import {useStickyHeader} from './StickyHeaderContext';

type Props = {
  pageStack: {title: string; to: string}[];
  tag?: string | null;
  additionalContent?: React.ReactNode;
  stickyTitle?: string;
};

export default function PageTitle({pageStack, tag, additionalContent, stickyTitle}: Props) {
  const {pathname} = useLocation();
  const params = useParams();
  const ref = React.useRef<HTMLDivElement>(null);
  const {setHeaderSubtitle} = useStickyHeader();

  // Show subtitle in sticky header when this element scrolls out of view
  React.useEffect(() => {
    const el = ref.current;
    if (!el || !stickyTitle) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderSubtitle(entry.isIntersecting ? null : stickyTitle),
      {threshold: 0}
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      setHeaderSubtitle(null);
    };
  }, [stickyTitle, setHeaderSubtitle]);

  return (
    <div ref={ref} className="my-8 flex items-center text-xl">
      {pageStack.map(({title, to}, i) => {
        let content;
        const titleEl = <h1>{title}</h1>;
        Object.entries(params).forEach(([param, value]) => {
          if (value !== undefined) {
            to = to.replace(`:${param}`, value);
          }
        });
        if (pathname === to) {
          content = titleEl;
        } else {
          content = (
            <>
              <Link to={to} className={`${tw.link}`}>
                {titleEl}
              </Link>
              <ChevronRightIcon className="h-6 w-6 pt-1" />
            </>
          );
        }

        return (
          <span key={i} className="flex items-center">
            {content}
          </span>
        );
      })}
      {tag && (
        <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
          {tag}
        </span>
      )}
      {additionalContent && additionalContent}
    </div>
  );
}
